import { FC, memo, useEffect, useState } from "react";
import Graphic from "@arcgis/core/Graphic";
import MapView from "@arcgis/core/views/MapView";
import ArcGisMap from "../../../../components/Common/Map";
import { TNullable } from "../../../../common/types/common";
import { TRACTOR_MARKET_SYMBOL } from "../../../../components/Common/Map/constants";
import { TDevice } from "../../../../common/types";
import { getFormattedDate } from "../../../../helpers/date-time-helper";

import "./styles.module.scss";

interface Props {
    devices: TNullable<TDevice[]>;
    selectedDevice: TNullable<TDevice>;
}

const MapWithMarkers: FC<Props> = ({ devices, selectedDevice }) => {
    const [view, setView] = useState<TNullable<MapView>>(null);

    const onInit = ({ view }) => {
        setView(view);
    };

    useEffect(() => {
        if (view && devices?.length) {
            const pointGraphics = devices.map((item) => new Graphic({
                geometry: {
                    type: "point",
                    ...item.lastKnownLocation,
                } as any,
                symbol: TRACTOR_MARKET_SYMBOL,
                attributes: {
                    label: `${item.machineName} (#${item.serialNumber})\n Last active: ${item.lastActive ? getFormattedDate(item.lastActive) : ''}`,
                    id: item.serialNumber,
                },
                popupTemplate: {
                    title: `${item.machineName} (#${item.serialNumber})`,
                    content: `Last active: ${item.lastActive ? getFormattedDate(item.lastActive) : ''}`,
                },
            }));

            // Clear existing graphics before adding new ones
            view.graphics.removeAll();
            view.graphics.addMany(pointGraphics);

            view.when(() => { // View is ready now
                if (pointGraphics.length > 0) {
                    view.goTo({ center: pointGraphics }).then(() => {
                        view.goTo({ zoom: view.zoom - 1 }).catch(() => { console.log('GoTo aborted.'); });
                    }).catch(() => { console.log('GoTo aborted.'); });
                }

                view.on("click", async (event) => {
                    const hit = await view.hitTest(event);
                    const graphic = (hit.results?.[0] as any)?.graphic;

                    if (graphic) {
                        view.popup?.open({
                            location: graphic.geometry,
                            features: [graphic],
                        } as any);
                    }
                });
            });
        } else if (view && (!devices || devices.length === 0)) {
            // Clear graphics if no devices
            view.graphics.removeAll();
        }
    }, [view, devices]);

    useEffect(() => {
        if (view && selectedDevice) {
            const graphicElement = view.graphics.find((item): any => item?.attributes.id === selectedDevice.serialNumber);

            if (graphicElement) {
                view.goTo({ center: [graphicElement] })
                    .then(async () => {
                        view.popup?.open({
                            location: graphicElement.geometry,
                            features: [graphicElement],
                        } as any);
                    })
                    .catch(() => { console.log('GoTo aborted.'); });
            }
        }
    }, [view, selectedDevice]);

    return <ArcGisMap onInit={onInit} />;
};

export default memo(MapWithMarkers);

